import React, { useState, Fragment } from "react";

import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { postFormDataWithDotNet } from "../../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../../api/ajax-endpoints";
import Alert from "../../../NewComponents/TypedComponents/Alert/Alert.component";
import { errorHandler } from "../../../helpers/errorHandler";
import { ROLE_TYPES } from "../../../helpers/roleTypes";
import { appInsights } from "../../../config/appInsights";
import { RootState } from "../../../redux/store";

import {
  useInvestmentProductSubtypeDetails,
  useInvestmentProductSubtypes,
  useInvestmentProductTypes,
} from "../../../custom-hooks/useDebtFund";
import { onChangeFile } from "../../../helpers/convertToFormData";
import { handleSwalErrorAlert } from "../../../helpers";
import FundFormShell from "./FundForm/FundFormShell";
import InvestmentDetailsSection from "./FundForm/InvestmentDetailsSection";
import VisibilitySection from "./FundForm/VisibilitySection";
import FinancialsSection from "./FundForm/FinancialsSection";
import WithdrawalWindowSection from "./FundForm/WithdrawalWindowSection";
import { needsWithdrawalWindows } from "./FundForm/planType";
import { toFundFormData } from "./FundForm/buildFundPayload";
import { confirmDiscard } from "./FundForm/confirmDiscard";
import ReviewFundModal from "./FundForm/ReviewFundModal";
import CurrencyPicker from "./FundForm/CurrencyPicker";
import useInvestmentPlanTypes from "../../../custom-hooks/useInvestmentPlanTypes";
import { TInterestRateChart } from "../../../helpers/interestRateChart";

export type TInvestmentProductDetails = {
  externalWalletProvider: any;
  systemWalletProvider?: any;
  depositType: any;
  monthlyPrematureLiquidationRate: number;
  endOfTenurePrematureLiquidationRate: number;
  coreBankingProductId: number;
  coreBankingTenantId: string;
  currencyCode: string;
  label?: string;
  maximumInvestment: number;
  minimumInvestment: number;
  value?: string;
  /** Live funds only — whether this currency still takes new subscriptions. */
  isLocked?: boolean | string;
  id?: number;
  investmentInterestPaymentTypeId?: number;
  termsAndConditionId?: number;
  /** Savings deposits only — required for those, rejected for the rest. */
  interestRateChart?: TInterestRateChart;
};

export type TCreateInvestmentProductState = {
  id?: number;
  name: string;
  startDate: string;
  endDate: string;
  objective: string;
  issuers: string;
  description: string;
  targetInvestors: string;
  investmentProductTypeId: number | null;
  investmentProductSubTypeId: number | null;
  investmentProductSubTypeDetailId: number | null;
  investmentProductDetails: TInvestmentProductDetails[];
  isCustomerFacing: boolean;
  /** Not derivable from deposit type — Flexisave and Habit Save share one. */
  productTypeId?: number | null;
  withdrawalWindowInterval?: number | null;
  withdrawalWindowIntervalUnit?: number | null;
  gracePeriod?: number | null;
  termSheetFile?: {
    file: File | undefined;
    fileName: string;
  };
};

const CreateInvestmentProduct = () => {
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [saveDraftError, setSaveDraftError] = useState("");
  const [state, setState] = useState<TCreateInvestmentProductState>({
    name: "",
    startDate: "",
    endDate: "",
    objective: "",
    issuers: "",
    description: "",
    targetInvestors: "",
    investmentProductDetails: [],
    investmentProductTypeId: null,
    investmentProductSubTypeId: null,
    investmentProductSubTypeDetailId: null,
    isCustomerFacing: true,
    productTypeId: null,
    withdrawalWindowInterval: null,
    withdrawalWindowIntervalUnit: null,
    gracePeriod: null,
    termSheetFile: undefined,
  });

  const [activeSection, setActiveSection] = useState("investment-details");

  const [isPickingDocument, setIsPickingDocument] = useState(false);

  // Save opens the review; the review is what actually posts.
  const [isReviewing, setIsReviewing] = useState(false);

  const history = useHistory();

  const { loggedInUser } = useSelector(
    (state: RootState) => state?.authReducer,
  );

  // The details section owns these lookups and renders them; the page keeps
  // only their failure, so a broken dropdown says so at the top of the form
  // rather than looking like an empty list. React Query serves both callers
  // from one request.
  const { isError: investmentProductTypeError } = useInvestmentProductTypes();
  const { data: planTypes } = useInvestmentPlanTypes();
  const { isError: investmentProductSubTypeError } =
    useInvestmentProductSubtypes(state.investmentProductTypeId);
  const { isError: investmentProductSubTypeDetailError } =
    useInvestmentProductSubtypeDetails(state.investmentProductSubTypeId);

  const handleDropdownChange = (selectedOptions: any) => {
    const existingCurrencyCodes = state.investmentProductDetails.map(
      (detail: any) => detail.value,
    );
    // gets d value of the new currency you are adding => empty if U r removing currency
    const currencyToBeAdded = selectedOptions.filter(
      (option: any) => !existingCurrencyCodes.includes(option.value),
    );

    // gets d value of an existing currency you are removing from the list => empty if U r adding
    const currencyToBeRemoved = state.investmentProductDetails.filter(
      (detail: any) =>
        !selectedOptions.find((option: any) => option.value === detail.value),
    );

    setState({
      ...state,
      investmentProductDetails: [
        ...state.investmentProductDetails.filter(
          (detail: any) => !currencyToBeRemoved.includes(detail),
        ), // Keep options not removed
        ...currencyToBeAdded.map((option: { value: any; label: any }) => ({
          ...option,
          // Set here rather than waiting on the core banking product: the
          // payload reads `currencyCode`, and a currency chosen but never
          // opened used to post `undefined`.
          currencyCode: option.value,
          currency_code: option.value,
          label: option.label,
        })),
      ],
    });
  };

  const handleSubmitDraft = async () => {
    setLoading(true);

    const formData = toFundFormData(state);

    try {
      const response = await postFormDataWithDotNet(
        ajaxEndpoints.FUNDER_INVESTMENT_PRODUCT,
        formData,
      );

      setSuccessMessage(response.message);
      setTimeout(() => {
        history.push("/investments");
      }, 3000);
    } catch (error: any) {
      appInsights.trackException({
        exception: error,
        properties: {
          fileName: "CreateInvestmentProduct.jsx",
        },
      });

      setSaveDraftError(errorHandler(error));
      setTimeout(() => {
        setSaveDraftError("");
      }, 3000);
    }
    setLoading(false);
  };

  async function onChangeTermSheet(e: any) {
    if (isPickingDocument) return; // Prevent multiple invocations
    setIsPickingDocument(true); // Set document picking status to true
    const errorCallback = (error: any) => {
      handleSwalErrorAlert(error);
      appInsights.trackException({
        exception: new Error(error),
        properties: { fileName: "Financials.tsx" },
      });
      setIsPickingDocument(false); // Reset document picking status
    };

    const setInfoCallback = (file: File, fileName: string) => {
      setState({
        ...state,
        termSheetFile: {
          file,
          fileName,
        },
      });
      setIsPickingDocument(false); // Reset document picking status
    };

    await onChangeFile(e, setInfoCallback, errorCallback);
  }

  const updateInvestmentProductDetails = (
    updatedDetails: TInvestmentProductDetails,
  ) => {
    const updatedList = state.investmentProductDetails.map((item) =>
      item.value === updatedDetails.value ? updatedDetails : item,
    );

    setState({
      ...state,
      investmentProductDetails: updatedList,
    });
  };

  const RAIL_SECTIONS = [
    { id: "investment-details", label: "Investment details" },
    { id: "visibility", label: "Visibility & documents" },
    ...(needsWithdrawalWindows(state.productTypeId)
      ? [{ id: "withdrawal-windows", label: "Withdrawal windows" }]
      : []),
    { id: "financials", label: "Financials" },
  ];

  const canSubmit = loggedInUser?.roles?.includes(
    ROLE_TYPES.INVESTMENT_PRODUCT_SETUP,
  );

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Fragment>
      <FundFormShell
        crumb="Create new fund"
        title="Create New Fund"
        status={{ label: "Draft · not submitted" }}
        sections={RAIL_SECTIONS}
        activeSection={activeSection}
        onSelectSection={scrollToSection}
        railNote="A fund is created as a draft. An approver activates it before customers can subscribe."
        onCancel={() => confirmDiscard(() => history.push("/investments"))}
        onSubmit={() => setIsReviewing(true)}
        submitLabel={canSubmit ? "Save & Submit" : "Save"}
        submitting={loading}
      >
        {investmentProductTypeError ||
        investmentProductSubTypeError ||
        investmentProductSubTypeDetailError ? (
          <Alert message="Error fetching investment type details" />
        ) : null}
        {saveDraftError ? <Alert message={saveDraftError} /> : null}
        {successMessage ? (
          <Alert message={successMessage} type="success" />
        ) : null}

        <InvestmentDetailsSection
          step={`Step 1 of ${RAIL_SECTIONS.length}`}
          value={{
            name: state.name,
            productTypeId: state.productTypeId,
            investmentProductTypeId: state.investmentProductTypeId,
            investmentProductSubTypeId: state.investmentProductSubTypeId,
            investmentProductSubTypeDetailId:
              state.investmentProductSubTypeDetailId,
            startDate: state.startDate,
            endDate: state.endDate,
            description: state.description,
            objective: state.objective,
            issuers: state.issuers,
            targetInvestors: state.targetInvestors,
          }}
          onChange={(patch) => setState((prev) => ({ ...prev, ...patch }))}
        />

        <VisibilitySection
          step={`Step 2 of ${RAIL_SECTIONS.length}`}
          isCustomerFacing={state.isCustomerFacing}
          onVisibilityChange={(isCustomerFacing) =>
            setState((prev) => ({ ...prev, isCustomerFacing }))
          }
          termSheetName={state.termSheetFile?.fileName}
          onPickTermSheet={onChangeTermSheet}
        />

        {needsWithdrawalWindows(state.productTypeId) ? (
          <WithdrawalWindowSection
            step={`Step 3 of ${RAIL_SECTIONS.length}`}
            value={{
              interval: state.withdrawalWindowInterval,
              unit: state.withdrawalWindowIntervalUnit,
              gracePeriod: state.gracePeriod,
            }}
            onChange={(next) =>
              setState((prev) => ({
                ...prev,
                withdrawalWindowInterval: next.interval ?? null,
                withdrawalWindowIntervalUnit: next.unit ?? null,
                gracePeriod: next.gracePeriod ?? null,
              }))
            }
          />
        ) : null}

        <FinancialsSection
          step={`Step ${RAIL_SECTIONS.length} of ${RAIL_SECTIONS.length}`}
          details={state.investmentProductDetails}
          currencyPicker={
            <CurrencyPicker
              selected={state.investmentProductDetails.map((detail) => ({
                value: String(detail?.value ?? detail?.currencyCode),
                label: String(detail?.label ?? detail?.currencyCode),
              }))}
              onChange={handleDropdownChange}
            />
          }
          onChangeDetail={(detail, patch) =>
            updateInvestmentProductDetails({ ...detail, ...patch })
          }
        />
      </FundFormShell>

      <ReviewFundModal
        open={isReviewing}
        state={state}
        planTypeLabel={
          planTypes?.find((type) => type.id === Number(state.productTypeId))
            ?.description
        }
        submitLabel={canSubmit ? "Create fund" : "Save as draft"}
        submitting={loading}
        footerNote="Nothing is saved until you confirm."
        onClose={() => setIsReviewing(false)}
        onGoToSection={scrollToSection}
        onConfirm={() => {
          setIsReviewing(false);
          handleSubmitDraft();
        }}
      />
    </Fragment>
  );
};

export default CreateInvestmentProduct;
